import React, { useState, useCallback } from 'react';
import {
  PhotoIcon,
  DocumentIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  CloudArrowDownIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { PimMedia, MediaFilters } from '../../types/pim';

// Helper function to build full media URL
const getMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';

  // If URL already includes protocol, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // For relative URLs, prepend backend base URL
  const backendUrl = import.meta.env.DEV
    ? 'http://localhost:8000'
    : '';

  return `${backendUrl}${url}`;
};

interface MediaGalleryProps {
  media: PimMedia[];
  loading?: boolean;
  onEdit?: (media: PimMedia) => void;
  onDelete?: (media: PimMedia) => void;
  onDownload?: (media: PimMedia) => void;
  onPreview?: (media: PimMedia) => void;
  onSelect?: (media: PimMedia[]) => void;
  selectable?: boolean;
  selectedIds?: number[];
  filters?: MediaFilters;
  onFiltersChange?: (filters: MediaFilters) => void;
  className?: string;
}

export const MediaGallery: React.FC<MediaGalleryProps> = ({
  media,
  loading = false,
  onEdit,
  onDelete,
  onDownload,
  onPreview,
  onSelect,
  selectable = false,
  selectedIds = [],
  filters,
  onFiltersChange,
  className = ''
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<number[]>(selectedIds);

  const handleMediaSelect = useCallback((mediaItem: PimMedia) => {
    if (!selectable) return;

    setSelectedMedia(prev => {
      const newSelection = prev.includes(mediaItem.id)
        ? prev.filter(id => id !== mediaItem.id)
        : [...prev, mediaItem.id];
      
      const selectedMediaItems = media.filter(m => newSelection.includes(m.id));
      onSelect?.(selectedMediaItems);
      
      return newSelection;
    });
  }, [selectable, media, onSelect]);

  const handleSelectAll = useCallback(() => {
    if (!selectable) return;

    const allSelected = media.every(m => selectedMedia.includes(m.id));
    const newSelection = allSelected ? [] : media.map(m => m.id);
    
    setSelectedMedia(newSelection);
    const selectedMediaItems = media.filter(m => newSelection.includes(m.id));
    onSelect?.(selectedMediaItems);
  }, [selectable, media, selectedMedia, onSelect]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMediaIcon = (mediaItem: PimMedia) => {
    if (mediaItem.kind === 'image') {
      return <PhotoIcon className="h-6 w-6" />;
    }
    return <DocumentIcon className="h-6 w-6" />;
  };

  const MediaGridItem: React.FC<{ mediaItem: PimMedia }> = ({ mediaItem }) => (
    <div 
      className={`
        group relative bg-white rounded-lg shadow-sm border-2 transition-all duration-200 cursor-pointer
        ${selectedMedia.includes(mediaItem.id) 
          ? 'border-blue-500 ring-2 ring-blue-200' 
          : 'border-gray-200 hover:border-gray-300'
        }
      `}
      onClick={() => selectable ? handleMediaSelect(mediaItem) : onPreview?.(mediaItem)}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
        {mediaItem.kind === 'image' && (mediaItem.thumbnail_url || mediaItem.url) ? (
          <img
            src={getMediaUrl(mediaItem.thumbnail_url || mediaItem.url)}
            alt={mediaItem.alt_text || mediaItem.title || 'Media'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center ${mediaItem.kind === 'image' && (mediaItem.thumbnail_url || mediaItem.url) ? 'hidden fallback-icon' : ''}`}>
          {getMediaIcon(mediaItem)}
        </div>
        
        {/* Selection checkbox */}
        {selectable && (
          <div className="absolute top-2 left-2">
            <input
              type="checkbox"
              checked={selectedMedia.includes(mediaItem.id)}
              onChange={() => handleMediaSelect(mediaItem)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Actions overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200">
          <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onPreview && (
              <button
                onClick={(e) => { e.stopPropagation(); onPreview(mediaItem); }}
                className="p-1 bg-white rounded-full text-gray-600 hover:text-blue-600"
                title="Preview"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
            )}
            {onDownload && (
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(mediaItem); }}
                className="p-1 bg-white rounded-full text-gray-600 hover:text-green-600"
                title="Download"
              >
                <CloudArrowDownIcon className="h-4 w-4" />
              </button>
            )}
            {onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(mediaItem); }}
                className="p-1 bg-white rounded-full text-gray-600 hover:text-yellow-600"
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(mediaItem); }}
                className="p-1 bg-white rounded-full text-gray-600 hover:text-red-600"
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Media info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-gray-900 truncate">
          {mediaItem.title || mediaItem.original_name}
        </h3>
        <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
          <span>{formatFileSize(mediaItem.size)}</span>
          <span>{formatDate(mediaItem.created_at)}</span>
        </div>
        {mediaItem.tags && mediaItem.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {mediaItem.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                {tag}
              </span>
            ))}
            {mediaItem.tags.length > 2 && (
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                +{mediaItem.tags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const MediaListItem: React.FC<{ mediaItem: PimMedia }> = ({ mediaItem }) => (
    <div 
      className={`
        flex items-center p-4 bg-white border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors
        ${selectedMedia.includes(mediaItem.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
      `}
      onClick={() => selectable ? handleMediaSelect(mediaItem) : onPreview?.(mediaItem)}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selectedMedia.includes(mediaItem.id)}
          onChange={() => handleMediaSelect(mediaItem)}
          className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-4"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Thumbnail */}
      <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded overflow-hidden">
        {mediaItem.kind === 'image' && (mediaItem.thumbnail_url || mediaItem.url) ? (
          <img
            src={getMediaUrl(mediaItem.thumbnail_url || mediaItem.url)}
            alt={mediaItem.alt_text || mediaItem.title || 'Media'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center ${mediaItem.kind === 'image' && (mediaItem.thumbnail_url || mediaItem.url) ? 'hidden fallback-icon' : ''}`}>
          {getMediaIcon(mediaItem)}
        </div>
      </div>

      {/* Media info */}
      <div className="flex-1 ml-4">
        <h3 className="text-sm font-medium text-gray-900">
          {mediaItem.title || mediaItem.original_name}
        </h3>
        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
          <span>{formatFileSize(mediaItem.size)}</span>
          <span className="flex items-center">
            <CalendarIcon className="h-4 w-4 mr-1" />
            {formatDate(mediaItem.created_at)}
          </span>
          {mediaItem.upload_by && (
            <span className="flex items-center">
              <UserIcon className="h-4 w-4 mr-1" />
              User {mediaItem.upload_by}
            </span>
          )}
        </div>
        {mediaItem.tags && mediaItem.tags.length > 0 && (
          <div className="mt-2 flex items-center">
            <TagIcon className="h-4 w-4 text-gray-400 mr-1" />
            <div className="flex flex-wrap gap-1">
              {mediaItem.tags.map((tag, index) => (
                <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        {onPreview && (
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(mediaItem); }}
            className="p-1 text-gray-400 hover:text-blue-600"
            title="Preview"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
        )}
        {onDownload && (
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(mediaItem); }}
            className="p-1 text-gray-400 hover:text-green-600"
            title="Download"
          >
            <CloudArrowDownIcon className="h-4 w-4" />
          </button>
        )}
        {onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(mediaItem); }}
            className="p-1 text-gray-400 hover:text-yellow-600"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(mediaItem); }}
            className="p-1 text-gray-400 hover:text-red-600"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {selectable && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={media.length > 0 && media.every(m => selectedMedia.includes(m.id))}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">
                {selectedMedia.length} of {media.length} selected
              </span>
            </div>
          )}
          <span className="text-sm text-gray-500">
            {media.length} media files
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Filters"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
          
          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                <div className="bg-current w-1.5 h-1.5 rounded-sm"></div>
                <div className="bg-current w-1.5 h-1.5 rounded-sm"></div>
                <div className="bg-current w-1.5 h-1.5 rounded-sm"></div>
                <div className="bg-current w-1.5 h-1.5 rounded-sm"></div>
              </div>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <div className="space-y-0.5 w-4 h-4">
                <div className="bg-current w-full h-1 rounded-sm"></div>
                <div className="bg-current w-full h-1 rounded-sm"></div>
                <div className="bg-current w-full h-1 rounded-sm"></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && onFiltersChange && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
              <select
                value={filters?.kind || ''}
                onChange={(e) => onFiltersChange({ ...filters, kind: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All types</option>
                <option value="image">Images</option>
                <option value="document">Documents</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
              <select
                value={filters?.is_public?.toString() || ''}
                onChange={(e) => onFiltersChange({ 
                  ...filters, 
                  is_public: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All</option>
                <option value="true">Public</option>
                <option value="false">Private</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Media grid/list */}
      {media.length === 0 ? (
        <div className="text-center py-32">
          <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No media files</h3>
          <p className="text-gray-500">Upload some files to get started.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {media.map(mediaItem => (
            <MediaGridItem key={mediaItem.id} mediaItem={mediaItem} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {media.map(mediaItem => (
            <MediaListItem key={mediaItem.id} mediaItem={mediaItem} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaGallery;